/**
 * Briques d'interface partagées.
 *
 * Le principe qui gouverne ce fichier : **le dashboard doit rester utilisable
 * quand l'API est éteinte**. Chaque bloc de contenu passe par `<Async>`, qui a
 * trois sorties possibles — chargement, erreur explicite, contenu — et aucune
 * quatrième où l'écran resterait blanc.
 */

import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';
import type { AsyncState } from '../lib/useApi';

// ─── Carte ──────────────────────────────────────────────────────────────────

export function Card(props: {
  title?: string;
  /** Phrase qui dit ce que la carte mesure — reprise mot pour mot de DATA-MODEL §3. */
  hint?: ReactNode;
  /** Contrôle aligné à droite du titre (segmenté, fraîcheur, bouton). */
  action?: ReactNode;
  children: ReactNode;
}): React.ReactElement {
  const { title, hint, action, children } = props;
  return (
    <section className="card">
      {title || action ? (
        <header className="card-head">
          <div>
            <h2>{title}</h2>
            {hint ? <p>{hint}</p> : null}
          </div>
          {action}
        </header>
      ) : null}
      {children}
    </section>
  );
}

// ─── Tuile KPI ──────────────────────────────────────────────────────────────

/*
 * Passe bento (2026-08-24) — trois affordances ajoutées, et une règle d'usage
 * pour chacune. Elles sont additives : une tuile qui n'en utilise aucune se rend
 * exactement comme avant.
 *
 *  · `featured` — la tuile pleine, bleue. UNE SEULE par grille : c'est le
 *    chiffre autour duquel la garde s'organise. Deux tuiles pleines, et il n'y
 *    a plus de premier regard.
 *  · `trend` — la variation par rapport à la période précédente. À ne poser que
 *    si la comparaison a un sens ET qu'elle est CALCULÉE : « athlètes équipés »
 *    ne peut que monter, et un écart inventé vaut moins que pas d'écart du tout.
 *  · `href` — la flèche d'ouverture en haut à droite. Seulement quand il existe
 *    vraiment un détail derrière ; une flèche qui ne mène nulle part se clique
 *    une fois et décrédibilise toutes les autres.
 */

export interface KpiTrend {
  /** Variation en part (0,12 = +12 %). Le signe décide de la couleur. */
  delta: number;
  /** Ce à quoi on compare, en toutes lettres : « vs période précédente ». */
  label: string;
  /**
   * Sens de lecture de la variation :
   *  · `true` (défaut) — monter est une bonne nouvelle ;
   *  · `false` — descendre est une bonne nouvelle (signalements ouverts, retards) ;
   *  · `null` — ni l'un ni l'autre, l'écart s'affiche en GRIS.
   *
   * Le troisième cas n'est pas un raffinement : une durée de visite qui baisse
   * peut vouloir dire « l'athlète a trouvé son horaire plus vite » comme
   * « l'athlète a renoncé ». La peindre en rouge affirmerait ce qu'on ne sait pas.
   */
  upIsGood?: boolean | null;
}

export function Kpi(props: {
  label: string;
  value: string;
  /** Définition exacte de la métrique — évite qu'on lise « connectés » comme « comptes ». */
  hint?: ReactNode;
  live?: boolean;
  /** `false` coupe l'animation du point : plus personne n'est connecté. */
  livePulsing?: boolean;
  /** Tuile pleine, bleue — une seule par grille. */
  featured?: boolean;
  trend?: KpiTrend;
  /** Ancre de détail : affiche la flèche en haut à droite. */
  href?: string;
  children?: ReactNode;
}): React.ReactElement {
  const { label, value, hint, live = false, livePulsing = true, featured, trend, href, children } = props;

  // La couleur suit le SENS, pas le signe. `upIsGood: false` sert aux compteurs
  // qu'on veut voir baisser ; `null` laisse l'écart en gris.
  const upIsGood = trend?.upIsGood === undefined ? true : trend.upIsGood;
  const trendClass =
    upIsGood === null ? 'kpi-trend' : `kpi-trend is-${(trend?.delta ?? 0) >= 0 === upIsGood ? 'up' : 'down'}`;

  return (
    <div className={featured ? 'kpi is-featured' : 'kpi'}>
      <span className="kpi-label">
        {live ? <i className={`pulse${livePulsing ? '' : ' is-off'}`} aria-hidden="true" /> : null}
        {label}
      </span>
      <span className={`kpi-value${live ? ' is-live' : ''}`}>{value}</span>
      {trend ? (
        <span className={trendClass}>
          <b>
            <span aria-hidden="true">{trend.delta >= 0 ? '↗' : '↘'}</span>
            {formatDelta(trend.delta)}
          </b>
          {trend.label}
        </span>
      ) : null}
      {hint ? <span className="kpi-hint">{hint}</span> : null}
      {children ? <span className="kpi-spark">{children}</span> : null}
      {href ? (
        <a className="kpi-arrow" href={href} aria-label={`Détail : ${label}`}>
          <span aria-hidden="true">↗</span>
        </a>
      ) : null}
    </div>
  );
}

function formatDelta(delta: number): string {
  const pct = Math.abs(delta) * 100;
  // Sous 10 %, un chiffre après la virgule : passer de 4,5 % à 5 % efface
  // justement l'écart qu'on cherchait à montrer.
  const shown = pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
  return `${delta >= 0 ? '+' : '−'}${String(shown).replace('.', ',')} %`;
}

// ─── Badges ─────────────────────────────────────────────────────────────────

export type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }): React.ReactElement {
  const cls = tone === 'neutral' ? 'badge' : `badge badge-${tone}`;
  return <span className={cls}>{children}</span>;
}

export function Banner(props: { tone: 'warn' | 'bad' | 'info'; title: string; children: ReactNode }): React.ReactElement {
  return (
    <div className={`banner banner-${props.tone}`} role={props.tone === 'bad' ? 'alert' : 'status'}>
      <div>
        <strong>{props.title}</strong>
        {props.children}
      </div>
    </div>
  );
}

// ─── États ──────────────────────────────────────────────────────────────────

export function Loading({ label = 'Chargement…' }: { label?: string }): React.ReactElement {
  return (
    <div className="state state-loading" role="status">
      <span className="state-title">{label}</span>
      <div className="skeleton" style={{ height: 10, width: '60%' }} />
      <div className="skeleton" style={{ height: 10, width: '85%' }} />
      <div className="skeleton" style={{ height: 10, width: '40%' }} />
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: ApiError; onRetry?: () => void }): React.ReactElement {
  return (
    <div className="state state-error" role="alert">
      <span className="state-title">{error.userMessage}</span>
      {error.offline ? (
        <span>
          Vérifiez que le serveur du back-office écoute sur <code className="mono">localhost:4000</code>. Les
          autres pages restent consultables.
        </span>
      ) : null}
      {error.problem.detail ? <span className="state-detail">{error.problem.detail}</span> : null}
      {onRetry ? (
        <div>
          <button type="button" className="btn btn-sm" onClick={onRetry}>
            Réessayer
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Empty({ children = 'Aucune donnée sur la période sélectionnée.' }: { children?: ReactNode }): React.ReactElement {
  return (
    <div className="state state-empty">
      <span className="state-title">Aucune donnée</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Rend le contenu à partir d'un `AsyncState`.
 *
 * Subtilité voulue : quand une donnée a déjà été chargée, un rechargement
 * périodique **ne** repasse **pas** par l'écran de chargement. Sinon le
 * dashboard clignoterait toutes les 30 secondes et deviendrait illisible.
 */
export function Async<T>(props: {
  state: AsyncState<T>;
  children: (data: T) => ReactNode;
  loadingLabel?: string;
}): React.ReactElement {
  const { state, children, loadingLabel } = props;
  if (state.data !== null) return <>{children(state.data)}</>;
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.loading) return <Loading label={loadingLabel} />;
  return <Empty />;
}

/**
 * Variante à deux sources : un graphique qui croise deux métriques (actifs et
 * nouveaux, alertes posées et déclenchées) n'a de sens que si les DEUX sont là.
 * Afficher la moitié du dessin serait pire qu'un état de chargement.
 */
export function Async2<A, B>(props: {
  a: AsyncState<A>;
  b: AsyncState<B>;
  children: (a: A, b: B) => ReactNode;
}): React.ReactElement {
  const { a, b, children } = props;
  if (a.data !== null && b.data !== null) return <>{children(a.data, b.data)}</>;
  const failed = a.error ? a : b.error ? b : null;
  if (failed && failed.error) return <ErrorState error={failed.error} onRetry={failed.reload} />;
  if (a.loading || b.loading) return <Loading />;
  return <Empty />;
}

/** Indicateur discret « dernière mise à jour » + bouton de rechargement manuel. */
export function Freshness(props: { state: AsyncState<unknown>; sinceLabel: string }): React.ReactElement {
  return (
    <span className="row small muted">
      {props.state.loading ? <span>actualisation…</span> : <span>{props.sinceLabel}</span>}
      <button type="button" className="btn btn-sm" onClick={props.state.reload}>
        Actualiser
      </button>
    </span>
  );
}
